import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ButtonBase, Typography, Badge } from '@mui/material';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import ProfileSection from './ProfileSection';
import NotificationSection from './NotificationSection';
import { useGetUserProfileQuery } from 'services/userApi';
import { useGetConversationsQuery } from 'services/chat/chatAPIs';
import { useAppUserActiveClientsListingQuery } from 'services/user-management/app-users/appUsersAPIs';

// assets
import { IconMenu2 } from '@tabler/icons';
import { useNavigate } from 'react-router';
import ChatIcon from '@mui/icons-material/Chat';
// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = ({ handleLeftDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: userData, isSuccess } = useGetUserProfileQuery();
  // Poll so the unread badge updates live when a client sends (no realtime layer
  // on the backend) and drops automatically once a thread is opened (read).
  const { data: conversationsData } = useGetConversationsQuery(
    { from: 1, limit: 1500 },
    { pollingInterval: 5000 }
  );
  // Only count unread for ACTIVE-client chats. Expired clients' old unread isn't
  // shown in the Active list, so counting it made the badge look like a phantom
  // count you couldn't clear.
  const { data: allActiveClientsData } = useAppUserActiveClientsListingQuery({ limit: 2000, from: 0, searchName: '' });
  const totalUnreadCount = useMemo(() => {
    const rooms = conversationsData?.chatRooms || [];
    const activeIds = new Set((allActiveClientsData?.users || []).map((u) => String(u._id)));
    return rooms.reduce((sum, room) => {
      const parts = room?.chatRoom?.participants || [];
      const client = parts.find((p) => Number(p?.userType) === 3) || parts[0];
      const clientId = client?._id ? String(client._id) : '';
      return activeIds.has(clientId) ? sum + (room?.unreadCount || 0) : sum;
    }, 0);
  }, [conversationsData, allActiveClientsData]);
  return (
    <>
      {/* logo & toggler button */}
      <Box
        sx={{
          width: 185,
          display: 'flex',
          [theme.breakpoints.down('md')]: {
            width: 'auto'
          }
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <Box textAlign="center">
            <LogoSection />
          </Box>
        </Box>
        <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'background .15s ease-in-out',
              background: 'transparent',
              color: theme.palette.text.primary,
              '&:hover': {
                background: theme.palette.surface.hover
              }
            }}
            onClick={handleLeftDrawerToggle}
            color="inherit"
            aria-label="Toggle navigation"
          >
            <IconMenu2 stroke={1.5} size="1.3rem" />
          </Avatar>
        </ButtonBase>
      </Box>

      {/* header search */}
      {/* <SearchSection /> */}
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ flexGrow: 1 }} />
      {/* Add inbox icon */}
      <ButtonBase sx={{ borderRadius: '12px', overflow: 'visible' }} onClick={() => navigate('chat')}>
        <Badge
          color="error"
          badgeContent={totalUnreadCount > 0 ? (totalUnreadCount > 99 ? '99+' : totalUnreadCount) : 0}
          invisible={!totalUnreadCount}
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              right: 2,
              top: 2,
              fontSize: '0.6875rem',
              fontWeight: 700,
              // Ring in the header's own colour so the count separates from the icon.
              border: '2px solid',
              borderColor: 'surface.chrome'
            }
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'background .15s ease-in-out',
              background: 'transparent',
              color: theme.palette.text.primary,
              '&:hover': {
                background: theme.palette.surface.hover
              }
            }}
            color="inherit"
          >
            <ChatIcon fontSize="small" />
          </Avatar>
        </Badge>
      </ButtonBase>
      {/* notification & profile */}
      <NotificationSection />

      {isSuccess && (
        // Hidden below md: at 390px the full name, three icon buttons and the avatar all
        // competed for the bar and the name wrapped into the toggle.
        <Typography variant="subtitle1" sx={{ mx: 1.5, display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>
          {userData?.fullName}
        </Typography>
      )}

      <ProfileSection />
    </>
  );
};

Header.propTypes = {
  handleLeftDrawerToggle: PropTypes.func
};

export default Header;
